import {NavigateFunction} from 'react-router-dom';
import {getUserTokenOrRedirect} from '../../shared/services/auth.service';
import {TimeOnSiteCompanyDto, TimeOnSiteReportDto, TimeOnSiteStayDto} from './dtos/admin-time-on-site.dto';

/** A refused request: the server's message (not a platform admin, no such company). */
export class AdminTimeOnSiteError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'AdminTimeOnSiteError';
    this.status = status;
  }
}

async function timeOnSiteRequest<T>(navigate: NavigateFunction, path: string): Promise<T> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(
    `${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/admin/time-on-site${path}`,
    {method: 'GET', headers: {Authorization: `Bearer ${token}`}});
  if (!response.ok) {
    const body = await response.json().catch(() => null) as {message?: string} | null;
    throw new AdminTimeOnSiteError(response.status, body?.message || `Request failed (${response.status})`);
  }
  return await response.json() as T;
}

/** Every company with visits, most recent visit first. */
export const getTimeOnSiteCompanies = (navigate: NavigateFunction) =>
  timeOnSiteRequest<TimeOnSiteCompanyDto[]>(navigate, '/companies');

/** One company's time on site, by appointment and by job. */
export const getTimeOnSiteReport = (navigate: NavigateFunction, companyId: number) =>
  timeOnSiteRequest<TimeOnSiteReportDto>(navigate, `/companies/${companyId}`);

/** "45m", "2h", "1h 5m", to the nearest minute (the job clocks' stopped reading). */
export const formatSiteSeconds = (seconds: number) => {
  const whole = Math.max(0, Math.round(seconds / 60));
  const h = Math.floor(whole / 60);
  const m = whole - h * 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

const dayAndTime: Intl.DateTimeFormatOptions = {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'};
const timeOnly: Intl.DateTimeFormatOptions = {hour: 'numeric', minute: '2-digit'};

/** "Oct 20, 8:02 AM – 11:40 AM", "Oct 20, 8:02 AM – now", or both days when it ran past midnight. */
export const stayLine = (stay: TimeOnSiteStayDto, timeZone?: string) => {
  const zone = timeZone ? {timeZone} : {};
  const arrived = new Date(stay.arrivedAt);
  const day = (d: Date) => d.toLocaleDateString('en-US', {...zone, year: 'numeric', month: 'numeric', day: 'numeric'});
  let end = 'now';
  if (stay.leftAt) {
    const left = new Date(stay.leftAt);
    end = left.toLocaleString('en-US', {...zone, ...(day(left) === day(arrived) ? timeOnly : dayAndTime)});
  }
  return `${arrived.toLocaleString('en-US', {...zone, ...dayAndTime})} – ${end}`;
};

/** An appointment's booked time ("Oct 20, 8:00 AM"), or a job's scheduled day ("Oct 20, 2026"). */
export const whenLine = (appointmentTime: string | null, scheduledDate: string | null, timeZone?: string) => {
  if (appointmentTime) return new Date(appointmentTime).toLocaleString('en-US', {...(timeZone ? {timeZone} : {}), ...dayAndTime});
  if (scheduledDate) {
    const [y, m, d] = scheduledDate.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric'});
  }
  return null;
};

/** "InProgress" -> "In progress", "PendingReschedule" -> "Pending reschedule". */
export const statusWords = (status: string | null) =>
  status ? status.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase()).replace(/ (\w)/g, (_, c: string) => ` ${c.toLowerCase()}`) : null;
